import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { Loader2, Trash2, X, Mail, UserPlus, Share2 } from 'lucide-react';

export const DeleteDialog = ({ open, onOpenChange, onDelete, deleting, trip_input }) => (
  <AlertDialog open={open} onOpenChange={onOpenChange}>
    <AlertDialogContent>
      <AlertDialogHeader>
        <AlertDialogTitle className="font-serif text-xl">Delete this trip?</AlertDialogTitle>
        <AlertDialogDescription>
          Are you sure you want to delete your trip to <span className="font-semibold text-charcoal">{trip_input.destination_city}, {trip_input.destination_country}</span>? 
          This action cannot be undone and all your trip data will be permanently removed.
        </AlertDialogDescription>
      </AlertDialogHeader>
      <AlertDialogFooter>
        <AlertDialogCancel className="rounded-full" data-testid="cancel-delete-btn">Cancel</AlertDialogCancel>
        <AlertDialogAction
          onClick={onDelete}
          className="bg-red-600 hover:bg-red-700 text-white rounded-full"
          disabled={deleting}
          data-testid="confirm-delete-btn"
        >
          {deleting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Trash2 className="w-4 h-4 mr-2" />}
          Yes, Delete Trip
        </AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
);

export const ShareDialog = ({
  open,
  onOpenChange,
  shareEmails,
  setShareEmails,
  shareMessage,
  setShareMessage,
  onSendEmail,
  sendingEmail,
  trip_input,
}) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="sm:max-w-md bg-white/95 backdrop-blur-xl">
      <DialogHeader>
        <DialogTitle className="font-serif text-xl flex items-center gap-2 text-ocean">
          <Mail className="w-5 h-5 text-gold" />
          Share Itinerary via Email
        </DialogTitle>
        <DialogDescription>
          Share your {trip_input.destination_cities?.length > 1 
            ? trip_input.destination_cities.join(', ') 
            : trip_input.destination_city} trip with friends and family
        </DialogDescription>
      </DialogHeader>
      <div className="space-y-4 py-4">
        <div className="space-y-2">
          <Label htmlFor="emails" className="text-ocean font-medium">Email Addresses *</Label>
          <Input
            id="emails"
            type="text"
            placeholder="email@example.com, another@example.com"
            value={shareEmails}
            onChange={(e) => setShareEmails(e.target.value)}
            className="w-full border-ocean/20 focus:border-ocean"
          />
          <p className="text-xs text-charcoal/60">Separate multiple emails with commas</p>
        </div>
        <div className="space-y-2">
          <Label htmlFor="message" className="text-ocean font-medium">Personal Message (optional)</Label>
          <Input
            id="message"
            type="text"
            placeholder="Check out this amazing trip I planned!"
            value={shareMessage}
            onChange={(e) => setShareMessage(e.target.value)}
            className="w-full border-ocean/20 focus:border-ocean"
          />
        </div>
      </div>
      <DialogFooter className="flex-row gap-2 sm:justify-between">
        <Button
          variant="outline"
          onClick={() => { onOpenChange(false); setShareEmails(''); setShareMessage(''); }}
          className="rounded-full border-ocean/30 text-ocean hover:bg-ocean/5"
        >
          <X className="w-4 h-4 mr-2" /> Cancel
        </Button>
        <Button
          onClick={onSendEmail}
          disabled={sendingEmail || !shareEmails.trim()}
          className="bg-gold hover:bg-gold-light text-ocean font-semibold rounded-full shadow-lg"
        >
          {sendingEmail ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Mail className="w-4 h-4 mr-2" />}
          Send Email
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);

export const InviteDialog = ({
  open,
  onOpenChange,
  inviteEmail,
  setInviteEmail,
  inviteName,
  setInviteName,
  onInvite,
  inviting,
}) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="sm:max-w-md">
      <DialogHeader>
        <DialogTitle className="font-serif text-xl text-ocean">Invite Travel Companion</DialogTitle>
        <DialogDescription>Send an invitation to join this trip</DialogDescription>
      </DialogHeader>
      <div className="space-y-4 py-4">
        <div>
          <Label className="text-ocean font-medium">Email Address</Label>
          <Input value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)} placeholder="companion@email.com"
            className="mt-1" data-testid="invite-email-input" />
        </div>
        <div>
          <Label className="text-ocean font-medium">Name (optional)</Label>
          <Input value={inviteName} onChange={(e) => setInviteName(e.target.value)} placeholder="Their name"
            className="mt-1" data-testid="invite-name-input" />
        </div>
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-full">Cancel</Button>
        <Button onClick={onInvite} disabled={inviting || !inviteEmail}
          className="bg-gold hover:bg-gold-light text-ocean font-semibold rounded-full" data-testid="send-invite-btn">
          {inviting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <UserPlus className="w-4 h-4 mr-2" />}
          Send Invite
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);

export const ShareBudgetDialog = ({
  open,
  onOpenChange,
  shareBudgetEmails,
  setShareBudgetEmails,
  onShareBudget,
  sendingBudgetEmail,
  generatedItinerary,
  currencyMode,
  convertedBudget,
}) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="sm:max-w-md" data-testid="share-budget-dialog">
      <DialogHeader>
        <DialogTitle className="font-serif text-ocean">Share Budget with Companions</DialogTitle>
        <DialogDescription>Send the estimated trip budget to your travel companions via email.</DialogDescription>
      </DialogHeader>
      <div className="space-y-4 py-4">
        <div>
          <Label className="text-sm">Recipient Emails (comma-separated)</Label>
          <Input
            value={shareBudgetEmails}
            onChange={(e) => setShareBudgetEmails(e.target.value)}
            placeholder="email1@example.com, email2@example.com"
            className="mt-1"
            data-testid="share-budget-emails-input"
          />
        </div>
        {generatedItinerary?.estimated_total_budget && (
          <div className="bg-ocean/5 rounded-lg p-3 text-sm">
            <p className="font-medium text-ocean mb-1">Budget Preview:</p>
            {(() => {
              const b = currencyMode === 'home' && convertedBudget ? convertedBudget : generatedItinerary.estimated_total_budget;
              return (
                <div className="space-y-1 text-charcoal/70">
                  {b.accommodation && <p>Accommodation: {b.accommodation}</p>}
                  {b.food && <p>Food: {b.food}</p>}
                  {b.transport && <p>Transport: {b.transport}</p>}
                  {b.activities && <p>Activities: {b.activities}</p>}
                  {b.total && <p className="font-semibold text-ocean">Total: {String(b.total).replace(/\s*\(approx\.?[^)]*\)/gi, '')}</p>}
                </div>
              );
            })()}
          </div>
        )}
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
        <Button onClick={onShareBudget} disabled={sendingBudgetEmail}
          className="bg-gold hover:bg-gold-light text-ocean font-semibold rounded-full" data-testid="send-budget-btn">
          {sendingBudgetEmail ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Share2 className="w-4 h-4 mr-2" />}
          Send Budget
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);
